/**
 * Regular expression for matching quarter formats like "FY22 Q1", "Q1 FY22", "2022 Q1", "Q1 2022"
 */
export const regexQuarters =
  /\b(FY\d{2} Q[1-4]|Q[1-4] FY\d{2}|\d{4} Q[1-4]|Q[1-4] \d{4})\b/;

/**
 * Determine the appropriate label key based on chart type and sort axis
 * @param {string} chartType - The type of chart
 * @param {string} sortAxis - The sort axis ('calc_asc', 'calc_desc', or other)
 * @returns {string} The appropriate label key
 */
export function getLabelKey(chartType, sortAxis) {
  if (chartType === 'PieChart') {
    return 'value';
  }

  return sortAxis === 'calc_asc' || sortAxis === 'calc_desc'
    ? 'display_value'
    : 'row_label';
}

/**
 * @param {Object} obj - The chart data
 * @param {Object} meta
 * @param {string} chartType
 * @param {string} sortAxis
 * Get sortable values from a data object based on chart type and sort axis
 * A calculated sort axis will return the sum of all values in the object
 * A label composed of multiple values will return all the values composing the values
 */
function getSortableValues(obj, meta, chartType, sortAxis) {
  let sortableValues = [];

  if (chartType === 'PieChart') {
    sortableValues.push(obj.value);
  } else if (sortAxis === 'calc_asc' || sortAxis === 'calc_desc') {
    const sum = getValues(obj, meta).reduce((sum, val) => sum + val, 0);
    sortableValues.push(sum);
  } else {
    sortableValues = obj.row_label_plain_values ?? [];
  }

  return sortableValues;
}

/**
 * Get all values from a data item based on series labels in metadata
 * @param {Object} obj
 * @param {Object} meta
 * @returns {Array} The extracted values
 */
export function getValues(obj, meta) {
  const values = [];

  meta.seriesLabels?.forEach(label => {
    if (obj[label] !== undefined) {
      values.push(obj[label]);
    }
  });

  return values;
}

/**
 * Parse a value to a Date, handling regular dates and fiscal quarters
 * @param {*} value - The value to parse
 * @returns {Date|null} The parsed date or null if not a valid date
 */
function parseDate(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const str = String(value);

  // Try parsing as fiscal quarter first
  if (regexQuarters.test(str)) {
    try {
      return parseFiscalQuarterToDate(str);
    } catch {
      return null;
    }
  }

  // Try parsing as regular date
  const date = new Date(str);
  if (!isNaN(date.getTime())) {
    return date;
  }

  return null;
}

/**
 * Comparator function for sorting values with automatic type detection
 * Always returns values in ascending order
 * @param {*} aValue - First value to compare
 * @param {*} bValue - Second value to compare
 * @returns {number} Comparison result (-1, 0, or 1)
 */
function sortableValueComparator(aValue, bValue, meta) {
  // Handle null/undefined values
  if (
    (aValue === null || aValue === undefined) &&
    (bValue === null || bValue === undefined)
  ) {
    return 0;
  }
  if (aValue === null || aValue === undefined) {
    return 1;
  }
  if (bValue === null || bValue === undefined) {
    return -1;
  }

  // Check if both values are already numbers
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return aValue - bValue;
  }

  // Convert to strings for further comparison
  const aStr = String(aValue);
  const bStr = String(bValue);

  // Keep status order unchanged for status so they appear in the right order
  if (meta?.reportColumnNames?.label?.toLowerCase()?.includes('status')) {
    return 1;
  }

  // Check if they're dates (including fiscal quarters)
  const aDate = parseDate(aStr);
  const bDate = parseDate(bStr);

  if (aDate && bDate) {
    return aDate.getTime() - bDate.getTime();
  }

  // Default to string comparison
  return aStr.localeCompare(bStr);
}

export function sortDataByType(data, meta, chartType, sortAxis) {
  if (
    !sortAxis ||
    sortAxis === 'none' ||
    chartType === 'ScatterPlot' ||
    chartType === 'ScatterBubblePlot'
  ) {
    return data;
  }

  const isDescending = sortAxis === 'desc' || sortAxis === 'calc_desc';

  return [...data].sort((a, b) => {
    const aValues = getSortableValues(a, meta, chartType, sortAxis);
    const bValues = getSortableValues(b, meta, chartType, sortAxis);

    // Compare arrays element by element until we find a difference
    const minLength = Math.min(aValues.length, bValues.length);

    for (let i = 0; i < minLength; i++) {
      const comparison = sortableValueComparator(aValues[i], bValues[i], meta);
      if (comparison !== 0) {
        return isDescending ? -comparison : comparison;
      }
    }

    // If all compared elements are equal, sort by array length
    const lengthComparison = aValues.length - bValues.length;
    return isDescending ? -lengthComparison : lengthComparison;
  });
}

/**
 * Sum all display values in an object
 * @param {Object} obj - The object containing display values
 * @returns {number} The sum of all display values
 */
export function sumDisplayValues(obj) {
  return Object.keys(obj)
    .filter(key => key.includes('display_value'))
    .reduce((sum, key) => {
      return sum + (Number(obj[key]) || 0);
    }, 0);
}

/**
 * Determine the data type for an axis based on data and metadata
 * @param {Array} data - The chart data
 * @param {string} chartType - The type of chart
 * @param {Object} meta - The chart metadata
 * @returns {string} The detected data type ('number', 'date', 'string', or 'status')
 */
export function determineDataTypeForAxis(data, chartType, meta) {
  const datum = data.find(obj => {
    // Pass empty string for sortAxis,  we just want the label values for the axis, sortAxis helps to get the calcutation values, which we don't need here
    const values = getSortableValues(obj, meta, chartType, '');
    return values.length > 0;
  });

  if (!datum) {
    return 'composite';
  }

  const sortableValues = getSortableValues(datum, meta, chartType, '');

  if (sortableValues.length > 1) {
    return 'composite';
  }

  const sortableValue = sortableValues[0];

  if (meta?.reportColumnNames?.label?.toLowerCase()?.includes('status')) {
    return 'status';
  }

  if (typeof sortableValue === 'number') {
    return 'number';
  }

  const sortableStr = String(sortableValue);

  if (parseDate(sortableStr)) {
    return 'date';
  }

  return 'string';
}

/**
 * Determine the data type for an axis based on data and metadata
 * @param {Array} data - The chart data
 * @param {string} chartType - The type of chart
 * @param {Object} meta - The chart metadata
 * @param {string} sortAxis - The sort axis (optional)
 * @returns {string} The detected data type ('number', 'date', 'string', or 'status')
 */
export function determineDataTypeForAxisOld(data, chartType, meta, sortAxis) {
  const labelKey = getLabelKey(chartType, sortAxis);
  const sortableData = data;

  const firstValidItem = sortableData.find(item => Boolean(item[labelKey]));

  let dataType = firstValidItem
    ? detectDataType(firstValidItem[labelKey])
    : 'string';

  // Check if it's a status field
  if (
    meta?.reportColumnNames?.label?.toLowerCase()?.includes('status') &&
    (!sortAxis || !sortAxis?.includes('calc'))
  ) {
    dataType = 'status';
  }

  // Force number type for calculation-based sorting
  if (sortAxis?.includes('calc')) {
    dataType = 'number';
  }

  return dataType;
}

/**
 * Sort data based on data type and sort direction
 * @param {Array} data - The data to sort
 * @param {Object} meta - The chart metadata
 * @param {string} chartType - The type of chart
 * @param {string} sortAxis - The sort axis ('asc', 'desc', 'calc_asc', 'calc_desc', 'none')
 * @returns {Array} The sorted data
 */
export function sortDataByTypeOld(data, meta, chartType, sortAxis) {
  // Determine data type using the utility function
  const dataType = determineDataTypeForAxisOld(data, chartType, meta, sortAxis);

  if (!sortAxis || sortAxis === 'none' || chartType === 'ScatterPlot') {
    return data;
  }

  const labelKey = getLabelKey(chartType, sortAxis);

  return [...data].sort((a, b) => {
    let aValue = a[labelKey];
    let bValue = b[labelKey];

    if (sortAxis === 'calc_desc' || sortAxis === 'calc_asc') {
      aValue = sumDisplayValues(a);
      bValue = sumDisplayValues(b);
    }

    switch (dataType) {
      case 'number':
        return sortAxis === 'desc' || sortAxis === 'calc_desc'
          ? Number(bValue) - Number(aValue)
          : Number(aValue) - Number(bValue);
      case 'date': {
        let aDate = new Date(aValue).getTime();
        let bDate = new Date(bValue).getTime();
        if (regexQuarters.test(aValue)) {
          aDate = parseFiscalQuarterToDate(aValue).getTime();
        }
        if (regexQuarters.test(bValue)) {
          bDate = parseFiscalQuarterToDate(bValue).getTime();
        }
        return sortAxis === 'desc' ? bDate - aDate : aDate - bDate;
      }
      case 'status':
        return sortAxis === 'desc' ? -1 : 1;
      default: // string
        if (
          (aValue === null || aValue === undefined) &&
          (bValue === null || bValue === undefined)
        ) {
          return 0;
        }
        if (aValue === null || aValue === undefined) {
          return 1;
        }
        if (bValue === null || bValue === undefined) {
          return -1;
        }

        if (sortAxis === 'desc') {
          return bValue.localeCompare(aValue);
        }
        return aValue.localeCompare(bValue);
    }
  });
}

/**
 * Detect the data type of a value (number, date, string, or status)
 * @param {*} value - The value to check
 * @returns {'number'|'date'|'string'|'status'} The detected data type
 */
export function detectDataType(value) {
  // Check if it's a valid number (including decimals)
  if (!isNaN(Number(value)) && value !== '') {
    return 'number';
  }

  // Convert value to string for regex testing
  const valueStr = String(value);

  // Check if it's a valid date
  const date = new Date(value);
  if (
    (date instanceof Date && !isNaN(date.getTime())) ||
    regexQuarters.test(valueStr)
  ) {
    return 'date';
  }

  // Default to string
  return 'string';
}

/**
 * Parse a fiscal quarter string (like "FY22 Q1" or "Q1 2022") into a Date object
 * @param {string} quarterStr - The quarter string to parse
 * @returns {Date} The date representing the start of the quarter
 */
export function parseFiscalQuarterToDate(quarterStr) {
  const match = quarterStr.match(/FY(\d{2})|(\d{4})|Q([1-4])/g);
  if (!match || match.length < 2) throw new Error('Invalid quarter format');

  const year = match[0].startsWith('FY')
    ? 2000 + parseInt(match[0].slice(2), 10)
    : parseInt(match[0], 10);

  // Find the quarter part (Q1, Q2, Q3, Q4)
  const quarterPart = match.find(m => m.startsWith('Q'));
  if (!quarterPart) throw new Error('Invalid quarter format: missing Q part');

  const quarter = parseInt(quarterPart.slice(1), 10);

  return new Date(year, (quarter - 1) * 3, 1);
}

/**
 * Get sort options based on data type and chart type
 * @param {string} type - The data type ('date', 'string', 'number', 'status', 'composite')
 * @param {string} chartType - The chart type
 * @returns {Array<Array<string|string>>} Array of sort options [label, value]
 */
export function getSortOptionsForType(type, chartType) {
  const defaultOption = ['Default', 'none'];

  let sortOptions;

  switch (type) {
    case 'date':
      sortOptions = [
        defaultOption,
        ['Axis values Newest to oldest', 'desc'],
        ['Axis values Oldest to newest', 'asc'],
      ];
      break;
    case 'string':
    case 'composite':
      sortOptions = [
        defaultOption,
        ['Axis values A to Z', 'asc'],
        ['Axis values Z to A', 'desc'],
      ];
      break;
    case 'number':
      sortOptions = [
        defaultOption,
        ['Axis values 1 to 9', 'asc'],
        ['Axis values 9 to 1', 'desc'],
      ];
      break;
    case 'status':
      sortOptions = [
        defaultOption,
        ['Axis values First to last', 'asc'],
        ['Axis values Last to first', 'desc'],
      ];
      break;
    default:
      sortOptions = [defaultOption];
  }

  if (!['ScatterBubblePlot', 'ScatterPlot'].includes(chartType)) {
    let dataValueLabel = 'Bar size';
    switch (chartType) {
      case 'LineChart':
        dataValueLabel = 'Line values';
    }

    sortOptions.push([`${dataValueLabel} 1 to 9`, 'calc_asc']);
    sortOptions.push([`${dataValueLabel} 9 to 1`, 'calc_desc']);
  }

  return sortOptions;
}

/**
 * Provide a display format large numbers with commas (e.g., 123,456)
 * @param  {number | string} value - value to be displayed
 */
export function formatDisplayValue(value) {
  // Javascript only supports 16 decimal places, so allow fractional numbers
  // to present in scientific notation
  if (typeof value === 'number' && value >= 1) {
    return value.toLocaleString('en');
  }
  return value;
}
